import type { TimeZone } from '@vvo/tzdb';
import { getTimeZones } from '@vvo/tzdb';

import DateTime from 'pkg/datetime';

interface TimeZoneCache {
	hour: number;
	zones: TimeZone[];
	byName: Map<string, TimeZone>;
}

const HourMs = 60 * 60 * 1000;

let cache: TimeZoneCache | null = null;

function timeZoneCache(): TimeZoneCache {
	const hour = Math.floor(Date.now() / HourMs);

	if (cache?.hour === hour) {
		return cache;
	}

	const zones = getTimeZones();
	const byName = new Map<string, TimeZone>();

	for (const tz of zones) {
		byName.set(tz.name, tz);
	}

	for (const tz of zones) {
		for (const alias of tz.group) {
			if (!byName.has(alias)) {
				byName.set(alias, tz);
			}
		}
	}

	cache = { hour, zones, byName };

	return cache;
}

/**
 *
 * @returns Array of all tzdb time zones
 */
export const getTimeZoneList = (): TimeZone[] => [...timeZoneCache().zones];

export const getGmtString = (tz: TimeZone): string | undefined => {
	if (tz?.currentTimeFormat && tz?.mainCities) {
		return `(GMT ${tz?.currentTimeFormat.substr(0, 6)}) ${tz?.mainCities.join(
			', '
		)}`;
	}

	return undefined;
};

/**
 * @returns Array of GMT strings from each unique time zone (e.g. (GMT +02:00) European Central Time)
 */
export const getGmtStrings = (): string[] =>
	getTimeZoneList()
		.sort((a, b) => a.currentTimeFormat.localeCompare(b.currentTimeFormat))
		.map((tz) => getGmtString(tz));

/**
 * @param tzString
 * @returns The tz object from a tz string
 */
export const getTimeZoneFromString = (tzString: string): TimeZone | undefined =>
	timeZoneCache().byName.get(tzString);

/**
 * @returns The tz object from the tz string in the session storage
 */
export const getTimeZoneFromSessionStorage = (): TimeZone =>
	getTimeZoneFromString(DateTime.getTimeZone());

/**
 *
 * @param tzString
 * @returns if tz string exists
 */
export const validateTimeZone = (tzString: string): boolean =>
	getGmtStrings().find((string) => string === tzString) ? true : false;

/**
 *
 * @param gmtString
 * @returns time zone from a gmt string
 */
export const getTimeZoneByGmtString = (gmtString: string): TimeZone => {
	const cities = gmtString
		.substr(gmtString.indexOf(')') + 2)
		.split(', ')
		.toString();
	return getTimeZoneList().find((tz) => tz.mainCities.toString() === cities);
};

/**
 *
 * @param tzName
 * @returns gmt string from tz name
 */
export const getGmtStringByTimeZoneName = (tzName: string): string =>
	getGmtString(getTimeZoneList().find((tz) => tz.name === tzName));
